/** sessionStorage key for the ERP exchange assertion (not a Raptor access JWT). */
export const ERP_EXCHANGE_ASSERTION_STORAGE_KEY = 'raptor.erpExchangeAssertion';

function readSearchParams(search?: string): URLSearchParams {
  const rawSearch = search ?? (typeof window !== 'undefined' ? window.location.search : '');
  return new URLSearchParams(rawSearch.startsWith('?') ? rawSearch.slice(1) : rawSearch);
}

/** Reads the ERP `Authorization` query (assertion), never `token`. */
export function readErpExchangeAssertionFromSearch(search?: string): string {
  const params = readSearchParams(search);
  return (params.get('Authorization') ?? params.get('authorization') ?? '').trim();
}

export function getStoredErpExchangeAssertion(): string {
  try {
    return sessionStorage.getItem(ERP_EXCHANGE_ASSERTION_STORAGE_KEY)?.trim() ?? '';
  } catch {
    return '';
  }
}

export function setStoredErpExchangeAssertion(assertion: string): void {
  try {
    const value = assertion.trim();
    if (!value) {
      sessionStorage.removeItem(ERP_EXCHANGE_ASSERTION_STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(ERP_EXCHANGE_ASSERTION_STORAGE_KEY, value);
  } catch {
    // ignore quota / private-mode
  }
}

export function clearStoredErpExchangeAssertion(): void {
  setStoredErpExchangeAssertion('');
}

export function resolveErpExchangeAssertion(search?: string): string {
  return readErpExchangeAssertionFromSearch(search) || getStoredErpExchangeAssertion();
}

/**
 * Persist ERP `?Authorization=` (exchange assertion) and strip it from the URL.
 * Must not write this value into TokenService / Bearer storage.
 */
export function absorbErpExchangeAssertion(options: {
  search?: string;
  replaceSearch?: (searchIncludingQuestionMarkOrEmpty: string) => void;
}): string {
  const params = readSearchParams(options.search);
  const queryAssertion = (params.get('Authorization') ?? params.get('authorization') ?? '').trim();
  if (!queryAssertion) {
    return getStoredErpExchangeAssertion();
  }

  setStoredErpExchangeAssertion(queryAssertion);

  if (options.replaceSearch) {
    params.delete('Authorization');
    params.delete('authorization');
    const next = params.toString();
    options.replaceSearch(next ? `?${next}` : '');
  }

  return queryAssertion;
}
